import { escapeHtml } from '../utils/escapeHtml';
import { appAlert, appConfirm, appPromptPassword } from '../utils/appDialog';
import {
  AppLocalStorageBackupError,
  applyAppLocalStorageBackup,
  summarizeBackupKeys,
  type AppLocalStorageBackupKey,
} from '../utils/appLocalStorageBackup';
import {
  parseAppLocalStorageBackupFromPaste,
  tryParseEncryptedBackupEnvelope,
} from '../utils/appLocalStorageBackupCrypto';

export type ConfigImportViewHooks = {
  onBack: () => void;
  /** Called after a successful import (e.g. reload or return to config). */
  onApplied?: () => void;
};

/** Paste and restore device localStorage settings exported from Config. */
export class ConfigImportView {
  render(container: HTMLElement, hooks: ConfigImportViewHooks): void {
    container.innerHTML = `
      <div class="config-view config-import-view">
        <header class="config-view-header">
          <p class="config-import-back">
            <button type="button" class="btn-ghost-dark config-back-link" id="config-import-back">← Back to Config</button>
          </p>
          <h2 class="file-title">Import device settings</h2>
          <p class="config-view-lead">
            Paste decrypted JSON from opening a <strong>Copy encrypted link</strong>, or a legacy encrypted backup file.
            Includes API keys — treat it like a password.
          </p>
        </header>
        <form class="config-view-form" id="config-import-form">
          <label class="config-label" for="config-import-json">Backup JSON</label>
          <textarea
            id="config-import-json"
            class="config-textarea"
            rows="14"
            spellcheck="false"
            autocomplete="off"
            placeholder='{ "format": "dailylife-localStorage-backup-encrypted", ... }'
          ></textarea>
          <label class="config-import-replace">
            <input type="checkbox" id="config-import-replace" checked />
            Replace existing settings on this device (removes keys not in the backup)
          </label>
          <div class="config-actions">
            <button type="submit" class="btn-primary" id="config-import-apply">Import settings</button>
          </div>
          <p class="config-status text-muted small" id="config-import-status" aria-live="polite"></p>
          <p class="config-import-preview text-muted small hidden" id="config-import-preview" aria-live="polite"></p>
        </form>
      </div>
    `;

    const form = container.querySelector<HTMLFormElement>('#config-import-form')!;
    const jsonInput = container.querySelector<HTMLTextAreaElement>('#config-import-json')!;
    const replaceInput = container.querySelector<HTMLInputElement>('#config-import-replace')!;
    const status = container.querySelector<HTMLElement>('#config-import-status')!;
    const preview = container.querySelector<HTMLElement>('#config-import-preview')!;

    const updatePreview = (): void => {
      const text = jsonInput.value.trim();
      if (!text) {
        preview.classList.add('hidden');
        preview.textContent = '';
        return;
      }
      if (tryParseEncryptedBackupEnvelope(text)) {
        preview.classList.remove('hidden');
        preview.textContent =
          'Encrypted device backup. Click Import settings and enter your passphrase.';
        return;
      }
      void (async () => {
        try {
          const backup = await parseAppLocalStorageBackupFromPaste(text);
          const keys = Object.keys(backup.entries) as AppLocalStorageBackupKey[];
          preview.classList.remove('hidden');
          preview.innerHTML = `Ready to import ${keys.length} setting(s): ${escapeHtml(summarizeBackupKeys(keys))}. Exported ${escapeHtml(backup.exportedAt)}. (Unencrypted legacy backup.)`;
        } catch (err) {
          preview.classList.remove('hidden');
          const msg =
            err instanceof AppLocalStorageBackupError ? err.message : 'Could not read backup.';
          preview.textContent = msg;
        }
      })();
    };

    jsonInput.addEventListener('input', updatePreview);

    container.querySelector('#config-import-back')!.addEventListener('click', () => {
      hooks.onBack();
    });

    form.addEventListener('submit', e => {
      e.preventDefault();
      void (async () => {
        status.textContent = '';
        const text = jsonInput.value.trim();
        let passphrase: string | undefined;
        if (tryParseEncryptedBackupEnvelope(text)) {
          const p = await appPromptPassword({
            title: 'Decrypt device settings',
            message: 'Enter the passphrase used when this backup was copied.',
            confirmLabel: 'Decrypt',
            fieldLabel: 'Passphrase',
          });
          if (!p) return;
          passphrase = p;
        }

        let backup;
        try {
          backup = await parseAppLocalStorageBackupFromPaste(text, passphrase);
        } catch (err) {
          const msg =
            err instanceof AppLocalStorageBackupError
              ? err.message
              : 'Could not parse backup.';
          status.textContent = msg;
          await appAlert(msg, 'Import failed');
          return;
        }

        const keys = Object.keys(backup.entries) as AppLocalStorageBackupKey[];
        const replace = replaceInput.checked;
        const ok = await appConfirm(
          replace
            ? `Replace device settings with ${keys.length} key(s) from the backup? This may sign you out if GitHub auth changes.`
            : `Merge ${keys.length} setting(s) from the backup into this device?`,
          {
            title: replace ? 'Replace settings?' : 'Import settings?',
            danger: replace,
            confirmLabel: replace ? 'Replace' : 'Import',
          }
        );
        if (!ok) return;

        try {
          const result = applyAppLocalStorageBackup(backup, replace ? 'replace' : 'merge');
          status.textContent = `Imported ${result.applied.length} setting(s).${
            result.removed.length ? ` Removed ${result.removed.length} other saved key(s).` : ''
          } Reload recommended.`;
          hooks.onApplied?.();
        } catch (err) {
          const msg =
            err instanceof AppLocalStorageBackupError
              ? err.message
              : 'Import failed.';
          status.textContent = msg;
          await appAlert(msg, 'Import failed');
        }
      })();
    });

    jsonInput.focus();
  }
}
